import { useMemo } from 'react'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'
import type { UseFiltersPayload } from '@domain-group/react-hooks'

import { MainContent, ModalContext, TopBar } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useMainNavigation from '@hooks/useMainNavigation'

import AssignTemplatePagePermissionsModal from '@modals/AssignTemplatePagePermissionsModal'
import ViewTemplatePageDetailsModal from '@modals/ViewTemplatePageDetailsModal'

import type {
  // @ts-expect-error TS2614
  ShowAssignTemplatePagePermissionsModal,
} from '@hooks/useTemplatePage'

import DashboardSearch from '@components/DashboardSearch'
import LoggedInUser from '@components/LoggedInUser'
import MainNavigation from '@sections/Admin/components/MainNavigation'

import PageContent from './components/PageContent'

export type PageContextPayload = UseFiltersPayload & {
  callbacks: {
    showAssignTemplatePagePermissionsModal: () => void,
    showViewTemplatePageDetailsModal: (payload: object) => void,
  },
}

const defaultState = {
  showAssignTemplatePagePermissionsModal: false,
  showViewTemplatePageDetailsModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    AssignTemplatePagePermissionsModal: {
      bulkAssignTemplatePagePermissions: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      closeModal: () => setState({ showAssignTemplatePagePermissionsModal: false }),
    },
    ViewTemplatePageDetailsModal: {
      closeModal: () => setState({ showViewTemplatePageDetailsModal: false }),
    },
  }

  return componentCallbacks[component] || {}
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const { showAssignTemplatePagePermissionsModal, showViewTemplatePageDetailsModal } = state

  const { callbacks: { toggleNavigation } } = useMainNavigation()

  const filtersPayload = useFilters({ persistFilters: true })
  const {
    callbacks: filterCallbacks,
    defaultFilters,
    defaultFiltersModifiedAt,
    pageFilters,
  } = filtersPayload

  const modalContext = useModals()
  const { callbacks: { setModalData } } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showAssignTemplatePagePermissionsModal: (payload: ShowAssignTemplatePagePermissionsModal) => {
        setModalData('AssignTemplatePagePermissionsModal', payload)
        setState({ showAssignTemplatePagePermissionsModal: true })
      },
      showViewTemplatePageDetailsModal: (payload: object) => {
        setModalData('ViewTemplatePageDetailsModal', payload)
        setState({ showViewTemplatePageDetailsModal: true })
      },
    },
    defaultFilters,
  }), [defaultFiltersModifiedAt]) // modalContext dependency required if layering modals

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <TopBar
          callbacks={{ toggleNavigation }}
          loggedInUserComponent={<LoggedInUser />}
          searchComponent={(
            <DashboardSearch
              searchEntities={['LayoutTemplate', 'TaskList', 'TemplatePage', 'TemplatePagePartial']}
            />
          )}
        />

        {/*
         // @ts-expect-error TS2739 */}
        <MainNavigation />

        <MainContent offset={{ top: TopBar.topBarHeight }}>
          {!showAssignTemplatePagePermissionsModal && !showViewTemplatePageDetailsModal
          && <PageContent pageFilters={pageFilters} />}
        </MainContent>

        <AssignTemplatePagePermissionsModal
          // @ts-expect-error TS2741
          callbacks={callbacks('AssignTemplatePagePermissionsModal', setState)}
          showModal={showAssignTemplatePagePermissionsModal}
        />

        <ViewTemplatePageDetailsModal
          callbacks={callbacks('ViewTemplatePageDetailsModal', setState)}
          requestOptions={{
            include_related_entity_owner: true,
            include_template_page_permission_ids: true,
            include_template_page_related_entities_where_subject: true,
          }}
          showModal={showViewTemplatePageDetailsModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
